import { supabase } from './supabase';
import { formatDate } from './dateUtils';

const SHARE_BASE_URL = 'https://lifelog.mobile/share';

function generateToken() {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let token = '';
  for (let i = 0; i < 24; i++) {
    token += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return token;
}

// 토큰 캐시 (같은 세션 내 재공유 시 즉시 응답)
const tokenCache = new Map();

export async function getOrCreateShareToken(recordId, userId) {
  if (!userId) throw new Error('로그인이 필요합니다.');

  // 메모리 캐시 확인
  if (tokenCache.has(recordId)) return tokenCache.get(recordId);

  const { data: existing } = await supabase
    .from('record_public_shares')
    .select('share_token')
    .eq('record_id', recordId)
    .maybeSingle();

  if (existing) {
    tokenCache.set(recordId, existing.share_token);
    return existing.share_token;
  }

  const token = generateToken();
  const { error } = await supabase
    .from('record_public_shares')
    .insert({ record_id: recordId, share_token: token, user_id: userId });

  if (error) throw error;
  tokenCache.set(recordId, token);
  return token;
}

function buildShareBody(record) {
  const emoji = record.category_emoji || '📦';
  const startFmt = formatDate(record.start_date);
  const dateText = record.is_ongoing
    ? `${startFmt} ~ 현재`
    : record.end_date
      ? `${startFmt} ~ ${formatDate(record.end_date)}`
      : startFmt;

  const lines = [
    '나의 Life를 공유합니다.',
    '',
    `제목 : ${record.title}`,
    `카테고리 : ${emoji} ${record.category_name || ''}`,
    `기간 : ${dateText}`,
  ];

  if (record.location_name) {
    lines.push(`위치 : 📍 ${record.location_name}`);
  }

  if (record.memo) {
    const memoPreview = record.memo.length > 40
      ? record.memo.slice(0, 40) + '...'
      : record.memo;
    lines.push(`메모 : ${memoPreview}`);
  }

  return lines.join('\n');
}

// 전체 텍스트 (클립보드용)
function buildFullText(record, shareUrl) {
  return `인생로그 - 나의 인생을 기록하다\n\n${buildShareBody(record)}\n\n${shareUrl}`;
}

function isMobile() {
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

export async function shareRecord(record, shareToken) {
  const shareUrl = `${SHARE_BASE_URL}/${shareToken}`;
  const shareBody = buildShareBody(record);

  // 1) 모바일에서만 Web Share API 시도 (PC는 공유 대상이 없어 에러 발생)
  if (isMobile() && navigator.share) {
    try {
      await navigator.share({
        title: '인생로그 - 나의 인생을 기록하다',
        text: shareBody,
        url: shareUrl,
      });
      return 'webshare';
    } catch (e) {
      if (e.name === 'AbortError') return 'cancelled';
      // 다른 에러는 클립보드 복사로 fallback
    }
  }

  // 2) 클립보드 복사 (PC 기본 동작)
  const fullText = buildFullText(record, shareUrl);
  try {
    await navigator.clipboard.writeText(fullText);
  } catch {
    const ta = document.createElement('textarea');
    ta.value = fullText;
    ta.style.cssText = 'position:fixed;opacity:0';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  }
  return 'clipboard';
}
